import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Slide } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Search from '@mui/icons-material/Search';
import { TextField } from '@totalsoft/rocket-ui';
import { useReactiveVar } from '@apollo/client';
import { dashboardSearchVar } from './variables';
import { useOnScreen } from 'hooks/ui/useOnScreen';
import { withAreaGetter } from 'providers/AreasConsumers';
import { areas } from 'constants/areas';
import Portal from 'utils/portal/Portal';
import { HeaderSearchContainer, MainSearchContainer } from './DashboardStyles';

const DashboardSearchComponent = ({ getElemByAreaKey }) => {
  const searchRef = useRef();
  const headerSearchRef = useRef();

  const onScreen = useOnScreen(searchRef, { topOffset: -50 });

  const search = useReactiveVar(dashboardSearchVar);

  const [focused, setFocused] = useState(false);

  useEffect(
    () => () => {
      dashboardSearchVar('');
    },
    []
  );

  const toggleFocus = useCallback(value => () => setFocused(value), []);

  // explicitly check for false, because before loading the value is null
  const showHeaderSearch = useMemo(() => onScreen === false, [onScreen]);

  return (
    <>
      <MainSearchContainer item xs={6} ref={searchRef}>
        {!showHeaderSearch && <SearchFieldComponent value={search} autoFocus focused={focused} toggleFocus={toggleFocus} />}
      </MainSearchContainer>
      <Portal container={getElemByAreaKey(areas.header)}>
        <Slide
          direction='up'
          container={() => document.getElementById('header-area')}
          in={showHeaderSearch}
          mountOnEnter
          unmountOnExit
          timeout={{ appear: 200, enter: 200, exit: 100 }}
        >
          <HeaderSearchContainer ref={headerSearchRef}>
            <SearchFieldComponent value={search} focused={focused} toggleFocus={toggleFocus} />
          </HeaderSearchContainer>
        </Slide>
      </Portal>
    </>
  );
};

DashboardSearchComponent.propTypes = {
  getElemByAreaKey: PropTypes.func
};

export default withAreaGetter(DashboardSearchComponent);

const SearchFieldComponent = ({ value, autoFocus, focused, toggleFocus }) => {
  const { t } = useTranslation();

  const fieldRef = useRef();

  useEffect(() => {
    const onSearchKeyDown = event => {
      if (event.code === 'F3') {
        event.preventDefault();
        const fieldComponent = fieldRef.current.getElementsByClassName('MuiInputBase-input')[0];
        fieldComponent.focus();
      } else if (event.code === 'Escape') {
        const fieldComponent = fieldRef.current.getElementsByClassName('MuiInputBase-input')[0];
        fieldComponent.blur();
      }
    };
    document.addEventListener('keydown', onSearchKeyDown);
    return () => {
      document.removeEventListener('keydown', onSearchKeyDown);
    };
  }, []);

  return (
    // workaround because rocket-ui doesn't do ref forwarding :(
    <div ref={fieldRef}>
      <TextField
        fullWidth
        label={t('Dashboard.MenuTitle')}
        onChange={dashboardSearchVar}
        value={value}
        placeholder='Search'
        isClearable
        debounceBy={200}
        startAdornment={<Search sx={{ mr: 1 }} />}
        autoFocus={autoFocus || focused}
        onFocus={toggleFocus(true)}
        onBlur={toggleFocus(false)}
      />
    </div>
  );
};

SearchFieldComponent.propTypes = {
  value: PropTypes.string,
  autoFocus: PropTypes.bool,
  focused: PropTypes.bool,
  toggleFocus: PropTypes.func
};
